/**
 * Olo Auth Listener Plugin
 * 
 * Listens for postMessage events from Olo Native Overlay iframe
 * and handles authentication success/failure
 */

export default function ({ app, store }) {
  // Only run on client-side
  if (process.server) {
    return
  }

  // Allowed origins for Olo postMessage
  const allowedOrigins = [
    'https://auth.olo.com',
    'https://auth.olosandbox.com',
    'https://app.oloauthsandbox.com',
    'https://app.oloauth.com',
    // Allow same-origin messages (for local testing and embedded overlays)
    window.location.origin,
  ]

  // Validate origin
  function isValidOrigin(origin) {
    // Allow same-origin messages (most common case for embedded overlays)
    if (typeof window !== 'undefined' && origin === window.location.origin) {
      return true
    }
    
    // Check against allowed origins list
    const isValid = allowedOrigins.some(allowed => {
      if (typeof allowed === 'string') {
        return origin.includes(allowed) || allowed.includes(origin)
      }
      return false
    })
    
    return isValid
  }

  // Handle postMessage from Olo iframe
  function handleOloMessage(event) {
    const timestamp = new Date().toISOString()
    
    try {
      // Validate origin
      if (!isValidOrigin(event.origin)) {
        return
      }

      // Parse message data
      let messageData = event.data
      
      // Handle array messages (sometimes postMessage sends arrays)
      if (Array.isArray(messageData) && messageData.length > 0) {
        messageData = messageData[0]
      }
      
      if (typeof messageData === 'string') {
        try {
          messageData = JSON.parse(messageData)
        } catch (e) {
          return
        }
      }

      // Handle different message types
      if (messageData && messageData.type === 'olo_auth_success') {
        console.log('[OloAuthListener] 📨 Received olo_auth_success message')
        handleAuthSuccess(messageData.data || messageData.payload || messageData.token)
      } else if (messageData && messageData.type === 'olo_auth_error') {
        console.log('[OloAuthListener] 📨 Received olo_auth_error message')
        handleAuthError(messageData.data || messageData.error)
      } else if (messageData && messageData.type === 'oloauth:token') {
        console.log('[OloAuthListener] 📨 Received oloauth:token message')
        handleAuthSuccess(messageData.data || messageData.token)
      } else if (messageData && messageData.type === 'oloauth:success') {
        console.log('[OloAuthListener] 📨 Received oloauth:success message')
        handleAuthSuccess(messageData.data?.token || messageData.data || messageData.token)
      } else if (messageData && messageData.type === 'borderless:success') {
        console.log('[OloAuthListener] 📨 Received borderless:success message')
        handleAuthSuccess(messageData.data || messageData.token)
      } else if (messageData && messageData.type === 'borderless:token') {
        console.log('[OloAuthListener] 📨 Received borderless:token message')
        handleAuthSuccess(messageData.data || messageData.token)
      } else if (messageData && (messageData.token || messageData.authtoken || messageData.access_token)) {
        // Fallback: check if message has token directly (no type field)
        handleAuthSuccess(messageData)
      } else if (messageData && messageData.status === 'ok' && messageData.data && (messageData.data.token || messageData.data.value || messageData.data.authtoken)) {
        // New fallback: generic { status: 'ok', data: { token }} shape
        handleAuthSuccess(messageData.data)
      } else if (messageData && messageData.data && typeof messageData.data === 'object' && (messageData.data.token || messageData.data.value || messageData.data.authtoken)) {
        // Generic nested data object with token fields
        handleAuthSuccess(messageData.data)
      } else if (messageData && messageData.raw && typeof messageData.raw === 'object' && (messageData.raw.token || messageData.raw.value || messageData.raw.authtoken)) {
        // Some formats may put token under raw
        handleAuthSuccess(messageData.raw)
      } else if (messageData && messageData.data && typeof messageData.data === 'string' && messageData.data.length > 20 && /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(messageData.data)) {
        // Looks like a JWT string inside data
        handleAuthSuccess({ token: messageData.data })
      }
    } catch (error) {
      // Silent error handling
      console.debug('[OloAuthListener] Error handling message:', error)
    }
  }

  // Handle successful authentication
  async function handleAuthSuccess(tokenData) {
    const timestamp = new Date().toISOString()
    const startTime = Date.now()
    
    console.log('[OloAuthListener] 🔐 handleAuthSuccess called', {
      hasData: !!tokenData,
      dataType: typeof tokenData,
      timestamp
    })
    
    try {
      // The tokenData should be the full token object
      // It should have: token, value, authtoken, provider, expires, raw, etc.
      // If it's just a string, wrap it in an object
      let tokenObject = tokenData
      
      if (typeof tokenData === 'string') {
        // If it's just a string, create a token object
        tokenObject = {
          token: tokenData,
          value: tokenData,
          authtoken: tokenData
        }
      } else if (tokenData && typeof tokenData === 'object') {
        // Already an object, use as is
        tokenObject = tokenData
      } else {
        console.error('[OloAuthListener] ❌ Invalid token data format')
        return
      }

      // Verify token object has at least one token field
      const hasToken = !!(tokenObject.token || tokenObject.value || tokenObject.authtoken)
      
      if (!hasToken) {
        console.error('[OloAuthListener] ❌ No token found in token object')
        return
      }

      // Transform token to boj-web expected format
      // boj-web expects: { access: { token: '...' }, ordering: { access: { token: '...' } }, id: '...' }
      // Overlay provides: { token: '...', value: '...', raw: { creds: { access_token: '...' }, member: { ... } } }
      let transformedToken = tokenObject
      
      if (tokenObject.raw && tokenObject.raw.creds && tokenObject.raw.creds.access_token) {
        // Extract access token from raw.creds
        const accessToken = tokenObject.raw.creds.access_token
        const memberId = tokenObject.raw.member?.id || tokenObject.raw.member?.mid || null
        
        // Transform to boj-web format
        transformedToken = {
          access: {
            token: accessToken
          },
          ordering: {
            access: {
              token: accessToken
            }
          },
          id: memberId,
          // Keep original data for reference
          _original: tokenObject
        }
      } else {
        // Fallback: try to use token/value directly
        const fallbackToken = tokenObject.token || tokenObject.value || tokenObject.authtoken
        if (fallbackToken) {
          transformedToken = {
            access: {
              token: fallbackToken
            },
            ordering: {
              access: {
                token: fallbackToken
              }
            },
            _original: tokenObject
          }
        } else {
          console.error('[OloAuthListener] ❌ Cannot transform token')
          return
        }
      }

      // Convert V2 token to V1 token (required by boj-web)
      // The overlay returns V2 token, but boj-web needs V1 token format
      
      // Extract the V2 token value (this is what we send to /api/v1/members/sso)
      const tokenV2Value = tokenObject.value || tokenObject.token || (tokenObject.raw && tokenObject.raw.creds && tokenObject.raw.creds.access_token)
      
      if (!tokenV2Value) {
        console.error('[OloAuthListener] ❌ Cannot convert token - no V2 value found')
        return
      }
      
      try {
        // Call /api/v1/members/sso to convert V2 to V1 (same as oauthcallback.vue does)
        const clientType = store.state.client || 'web'
        const deviceID = store.state.deviceId || null
        
        const tokenV1Response = await app.$axios({
          method: 'post',
          url: '/api/v1/members/sso',
          headers: {
            client_type: clientType,
            device_id: deviceID
          },
          data: {
            provider: 'oloauth',
            token: tokenV2Value
          }
        })
        
        const tokenV1 = tokenV1Response?.data?.tokens || null
        
        if (!tokenV1) {
          console.error('[OloAuthListener] ❌ Failed to convert token V2 to V1')
          return
        }
        
        console.log('[OloAuthListener] ✅ Token V2 converted to V1 successfully')
        
        // Save V1 token to Vuex (this is what getUser() expects)
        store.commit('setToken', tokenV1)
        console.log('[OloAuthListener] ✅ Token saved to Vuex store')

        // Fetch user profile (now using V1 token)
        console.log('[OloAuthListener] 📥 Fetching user profile...')
        const userLoaded = await fetchUserProfileWithFallback(tokenV1)
        if (!userLoaded) {
          console.error('[OloAuthListener] ❌ Failed to load user profile')
          return
        }
        
        console.log('[OloAuthListener] ✅ User profile loaded successfully')

        // Close overlay
        store.commit('setShowBorderlessOverlay', false)

        // Handle cart merge if needed
        await handleCartMerge()

        // Navigate based on context
        handleNavigation()

      } catch (error) {
        console.error('[OloAuthListener] Error converting token V2 to V1:', error)
        store.commit('setShowBorderlessOverlay', false)
        store.commit('setErrors', ['There was an error signing you in. Please try again.'])
      }
    } catch (error) {
      console.error('[OloAuthListener] Error in handleAuthSuccess:', error)
      store.commit('setShowBorderlessOverlay', false)
      store.commit('setErrors', ['There was an error signing you in. Please try again.'])
    }
  }

  // Fetch user profile (token should be V1 format already saved in store)
  async function fetchUserProfileWithFallback(tokenV1) {
    try {
      // getUser() reads from store.state.token.access.token
      // So we just need to call it - the token is already in the store
      const user = await app.$api.getUser()
      if (user) {
        store.commit('setUser', user)
        
        // Braze Integration: Check if user exists in Braze
        const alias = await app.$api.exportUserProfileById({
          email_address: user.email,
          fields_to_export: [
            'user_aliases',
            'email',
            'external_id',
            'custom_attributes'
          ]
        })

        const channel = store.state.channel
        
        if (!alias?.data?.users?.length) {
          // New user - trigger Registration_Completed event
          const registrationCompletedEvent =
            channel !== 'catering'
              ? 'Registration_Completed'
              : 'Registration_Complete_Catering'
          
          console.log('[OloAuthListener] 🆕 New user detected in Braze')
          
          // Mark as new user for opt-in modal
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('boj_isNewUser', 'true')
          }
          
          // Initialize Braze for new user
          app.$braze.changeUser(user.id)
          app.$braze.openSession()
          app.$braze.getUser().setEmail(user.email)
          app.$braze.getUser().setFirstName(user.first_name)
          app.$braze.getUser().setLastName(user.last_name)
          app.$braze
            .getUser()
            .setPhoneNumber(
              user.phone.length === 10 ? '+1' + user.phone : user.phone
            )
          app.$braze.logCustomEvent(registrationCompletedEvent, {
            User: user
          })
          
          // Set channel-specific external ID
          if (channel === 'catering') {
            app.$braze
              .getUser()
              .setCustomUserAttribute('catering_external_id', user.id)
          } else {
            app.$braze
              .getUser()
              .setCustomUserAttribute('default_external_id', user.id)
          }
        } else if (alias.data.message && alias.data.message === 'success') {
          // Existing user - handle Wisely alias migration
          const userAlias = alias.data.users.find((u) => {
            return u.user_aliases.find((_alias) => {
              return _alias.alias_label === 'wisely' && !u.external_id
            })
          })
          
          // Alias without External ID and Wisely Alias was found
          if (userAlias && userAlias.user_aliases?.length) {
            await app.$api.usersIdentify({
              aliases_to_identify: [
                {
                  external_id: user.id,
                  user_alias: userAlias.user_aliases[0]
                }
              ],
              merge_behavior: 'merge'
            })
          }
          
          // Login existing user in Braze
          const externalID = alias.data.users[0].external_id
          app.$braze.changeUser(externalID)
          app.$braze.openSession()
          
          console.log('[OloAuthListener] 👤 Existing user logged in to Braze')
          
          // Set channel-specific external ID
          if (channel === 'catering') {
            app.$braze
              .getUser()
              .setCustomUserAttribute('catering_external_id', user.id)
          } else {
            app.$braze
              .getUser()
              .setCustomUserAttribute('default_external_id', user.id)
          }
        }
        
        // CF - P2: Check if user needs to complete profile (opt-in pop-up)
        // Show modal ONLY for NEW USERS who are NOT opted in to BOTH email AND SMS
        const needsOptIn = !user.optin || !user.optin_sms
        
        // Check if this is a new user (just registered in this session)
        // New users will have a flag set in sessionStorage by oauthcallback.vue
        const isNewUser = typeof sessionStorage !== 'undefined' && 
                         sessionStorage.getItem('boj_isNewUser') === 'true'
        
        // Check if we've already shown the modal in this session
        const hasSeenModalThisSession = typeof sessionStorage !== 'undefined' && 
                                        sessionStorage.getItem('boj_completeProfileShown') === 'true'
        
        // Debug logs
        console.log('[OloAuthListener] 🔍 Complete Profile Modal Check:', {
          isNewUser,
          needsOptIn,
          hasSeenModalThisSession,
          userOptin: user.optin,
          userOptinSms: user.optin_sms
        })
        
        // Only show for NEW users who need opt-in AND haven't seen the modal in this session
        if (isNewUser && needsOptIn && !hasSeenModalThisSession) {
          console.log('[OloAuthListener] 📋 New user needs opt-in - showing Complete Profile modal')
          // Wait a bit to ensure page is fully loaded, then show modal
          setTimeout(() => {
            store.commit('setShowCompleteProfileModal', true)
            
            // Mark as shown in sessionStorage
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.setItem('boj_completeProfileShown', 'true')
            }
          }, 500)
        } else if (!isNewUser && needsOptIn) {
          console.log('[OloAuthListener] ℹ️ Existing user needs opt-in - NOT showing modal (only for new users)')
        } else if (hasSeenModalThisSession) {
          console.log('[OloAuthListener] ℹ️ Modal already shown in this session - NOT showing again')
        }
        
        return true
      }
      
      return false
    } catch (e) {
      console.error('[OloAuthListener] ❌ Error fetching user profile:', e.message)
      return false
    }
  }

  // Handle authentication error
  function handleAuthError(errorData) {
    store.commit('setShowBorderlessOverlay', false)
    
    const errorMessage = errorData?.message || 
                        errorData?.error || 
                        'There was an error signing you in. Please try again.'
    
    store.commit('setErrors', [errorMessage])
    console.debug('[OloAuthListener] Auth error:', errorMessage)
  }

  // Handle cart merge (guest cart → authenticated cart)
  async function handleCartMerge() {
    try {
      const order = store.state.order
      if (order && order.products && order.products.length > 0) {
        // The backend should handle cart merge automatically when user is authenticated
        // But we can trigger a cart refresh if needed
        try {
          await app.$api.getCart()
        } catch (e) {
          // Silent catch
        }
      }
    } catch (error) {
      // Don't throw - cart merge is not critical
      console.debug('[OloAuthListener] Cart merge error:', error)
    }
  }

  // Navigate user after successful login
  function handleNavigation() {
    try {
      const route = app.router.currentRoute
      const order = store.state.order

      // If user has items in cart, go to checkout
      if (order && order.products && order.products.length > 0) {
        app.router.push('/order/checkout')
        return
      }

      // If user was on login/signup page, go to account
      if (route.path.includes('/account/login') || route.path.includes('/account/signup')) {
        app.router.push('/account')
        return
      }

      // If on checkout, stay there (user just logged in during checkout)
      if (route.path.includes('/order/checkout')) {
        return
      }

      // Otherwise, stay on current page
    } catch (error) {
      console.debug('[OloAuthListener] Navigation error:', error)
    }
  }

  // Check if we should show the Complete Profile modal for already logged-in users
  function checkCompleteProfileModal() {
    // Only check if user is already logged in
    if (!store.state.user) {
      return
    }

    const user = store.state.user
    
    // Check if user needs to complete profile (opt-in pop-up)
    const needsOptIn = !user.optin || !user.optin_sms
    
    // Check if this is a new user (just registered in this session)
    const isNewUser = typeof sessionStorage !== 'undefined' && 
                     sessionStorage.getItem('boj_isNewUser') === 'true'
    
    // Check if we've already shown the modal in this session
    const hasSeenModalThisSession = typeof sessionStorage !== 'undefined' && 
                                    sessionStorage.getItem('boj_completeProfileShown') === 'true'
    
    // Debug logs
    console.log('[OloAuthListener] 🔍 Complete Profile Modal Check (on load):', {
      isNewUser,
      needsOptIn,
      hasSeenModalThisSession,
      userOptin: user.optin,
      userOptinSms: user.optin_sms
    })
    
    // Only show for NEW users who need opt-in AND haven't seen the modal in this session
    if (isNewUser && needsOptIn && !hasSeenModalThisSession) {
      console.log('[OloAuthListener] 📋 New user needs opt-in - showing Complete Profile modal')
      // Wait a bit to ensure page is fully loaded, then show modal
      setTimeout(() => {
        store.commit('setShowCompleteProfileModal', true)
        
        // Mark as shown in sessionStorage
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('boj_completeProfileShown', 'true')
        }
      }, 500)
    } else if (!isNewUser && needsOptIn) {
      console.log('[OloAuthListener] ℹ️ Existing user needs opt-in - NOT showing modal (only for new users)')
    } else if (hasSeenModalThisSession) {
      console.log('[OloAuthListener] ℹ️ Modal already shown in this session - NOT showing again')
    }
  }

  // Register event listener
  if (typeof window !== 'undefined') {
    window.addEventListener('message', handleOloMessage, false)
    
    // Check if we should show modal on initial load (for already logged-in users)
    checkCompleteProfileModal()
    
    // Watch for user changes (when user logs in or loads)
    store.watch(
      (state) => state.user,
      (newUser) => {
        if (newUser) {
          console.log('[OloAuthListener] 👤 User loaded, checking modal...')
          checkCompleteProfileModal()
        }
      }
    )
  }
}
